import { useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { CalendarDays } from 'lucide-react';
import { format, addDays } from 'date-fns';
import { fmtDay } from '@/lib/dates';

const ymd = (d) => format(d, 'yyyy-MM-dd');
// Programme dates follow Singapore time (same as the server).
export const todayYmd = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Singapore' }).format(new Date());
const plusDays = (s, n) => { const [y, m, d] = s.split('-').map(Number); return ymd(addDays(new Date(y, m - 1, d), n)); };
export const fmtStart = (s) => fmtDay(s, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });

// Today / Tomorrow / pick a date — the official programme start date.
export function StartDateChooser({ value, onChange, testid = 'start' }) {
  const t = todayYmd(), tm = plusDays(t, 1);
  const pill = (v, label) => (
    <button type="button" onClick={() => onChange(v)} data-testid={`${testid}-${label.toLowerCase()}`}
      className={`h-9 px-3 rounded-full border text-xs font-semibold transition-colors ${value === v ? 'border-[#00C8FF] bg-[#00C8FF]/15 text-[#00C8FF]' : 'border-[var(--c-hair)] hover:bg-[var(--c-film)]'}`}>{label}</button>
  );
  return (
    <div className="flex flex-wrap items-center gap-2">
      {pill(t, 'Today')}
      {pill(tm, 'Tomorrow')}
      <Input type="date" min={t} value={value} onChange={e => e.target.value && onChange(e.target.value)} data-testid={`${testid}-date-input`}
        className="h-9 w-[160px] bg-[var(--c-film)] border-[var(--c-hair)] text-sm" />
    </div>
  );
}

// Coach card for an already-active (or not-yet-started) programme: change its official start date.
export function ActiveProgrammeStart({ clientId, start, onChanged }) {
  const [val, setVal] = useState(start);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/clients/${clientId}/programme-start`, { start_date: val });
      toast.success(`Programme now starts ${fmtStart(val)} — unfinished sessions and meal days moved`);
      await onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not change the start date'); }
    finally { setBusy(false); }
  };
  return (
    <div className="regen-card p-4 space-y-3" data-testid="cd-programme-start">
      <div className="flex items-center gap-2 text-sm">
        <CalendarDays className="w-4 h-4 text-[#00C8FF]" />
        <span>Programme start date: <b data-testid="cd-programme-start-value">{fmtStart(start)}</b>{start > todayYmd() ? ' (not started yet)' : ''}</span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <StartDateChooser value={val} onChange={setVal} testid="cd-start" />
        <Button size="sm" disabled={busy || val === start || val < todayYmd()} onClick={save} data-testid="cd-start-save" className="bg-[#00C8FF] text-[#081726] font-semibold">
          {busy ? 'Saving…' : 'Change start date'}
        </Button>
      </div>
      <p className="text-[11px] text-muted-foreground">Training and nutrition move together. Completed sessions and client logs are kept; paid access is extended by the same number of days.</p>
    </div>
  );
}
